import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { Show, createMemo, createSignal } from "solid-js";
import { SkillChips } from "~/components/SkillChips";
import {
  expressOpportunityInterest,
  fetchOpportunity,
  fetchProfile,
  reportOpportunity,
  updateOpportunityStatus,
  type OpportunityStatus,
} from "~/lib/api";
import { profileQueryKey } from "~/lib/routes";

const TYPE_LABELS: Record<string, string> = {
  job: "Job",
  gig: "Gig",
  mentorship: "Mentorship",
  collab: "Collab",
  speaking: "Speaking",
};

export function OpportunityDetailPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const opportunityId = () => params().opportunityId ?? "";
  const [interestNote, setInterestNote] = createSignal("");

  const detailQuery = createQuery(() => ({
    queryKey: ["opportunity", opportunityId()],
    queryFn: () => fetchOpportunity(opportunityId()),
    enabled: !!opportunityId(),
  }));
  const myProfileQuery = createQuery(() => ({
    queryKey: profileQueryKey(),
    queryFn: () => fetchProfile(),
  }));

  const item = () => detailQuery.data?.data;
  const isOwn = createMemo(
    () => !!item()?.posted_by_user_id && item()?.posted_by_user_id === myProfileQuery.data?.data?.user_id,
  );
  const isOpen = () => item()?.status === "open";

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["opportunity", opportunityId()] });
    queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    queryClient.invalidateQueries({ queryKey: ["connections"] });
  }

  const interestMutation = createMutation(() => ({
    mutationFn: () => expressOpportunityInterest(opportunityId()),
    onSuccess: () => {
      setInterestNote("Interest sent — they will see a connection request.");
      invalidate();
    },
    onError: (err: unknown) => setInterestNote((err as Error).message),
  }));

  const statusMutation = createMutation(() => ({
    mutationFn: (status: OpportunityStatus) => updateOpportunityStatus(opportunityId(), status),
    onSuccess: () => invalidate(),
  }));

  const reportMutation = createMutation(() => ({
    mutationFn: (reason: string) => reportOpportunity(opportunityId(), reason),
    onSuccess: () => alert("Report submitted. The team will review it."),
    onError: (err: unknown) => alert((err as Error).message),
  }));

  function onReport() {
    const reason = window.prompt("Why are you reporting this opportunity? (required)");
    if (!reason?.trim()) return;
    reportMutation.mutate(reason.trim());
  }

  function goPoster() {
    const userId = item()?.posted_by_user_id;
    if (!userId) return;
    navigate({ to: "/profile/$profileId", params: { profileId: userId } });
  }

  return (
    <section class="card">
      <Show when={detailQuery.isPending}>
        <p class="muted">Loading opportunity…</p>
      </Show>
      <Show when={detailQuery.isError}>
        <div class="error-box">{(detailQuery.error as Error).message}</div>
        <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/opportunities" })}>
          Back to opportunities
        </button>
      </Show>
      <Show when={item()} keyed>
        {(opp) => (
          <>
            <p class="status-chip">
              {TYPE_LABELS[opp.type] ?? opp.type} · {opp.status}
            </p>
            <h1>{opp.title}</h1>
            <Show when={opp.sponsor_org}>
              <p class="profile-preview-headline">{opp.sponsor_org}</p>
            </Show>
            <p class="muted">
              {[opp.location, opp.remote_ok ? "Remote OK" : null].filter(Boolean).join(" · ") || "Location not listed"}
            </p>
            <Show when={opp.expires_at}>
              <p class="muted">Closes {new Date(opp.expires_at!).toLocaleDateString()}</p>
            </Show>
            <SkillChips skills={opp.skills} />
            <p class="profile-detail-bio">{opp.description}</p>
            <Show when={opp.apply_url}>
              <p>
                <a href={opp.apply_url} target="_blank" rel="noreferrer">
                  Apply link
                </a>
              </p>
            </Show>

            <Show when={interestNote()}>
              <p class="looking-for">{interestNote()}</p>
            </Show>

            <div class="wizard-actions">
              <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/opportunities" })}>
                Back
              </button>
              <button type="button" class="btn btn-ghost" onClick={goPoster}>
                View poster
              </button>
              <Show when={!isOwn() && isOpen()}>
                <button
                  type="button"
                  class="btn btn-primary"
                  disabled={interestMutation.isPending}
                  onClick={() => interestMutation.mutate()}
                >
                  {interestMutation.isPending ? "Sending…" : "I'm interested"}
                </button>
              </Show>
              <Show when={!isOwn()}>
                <button type="button" class="btn btn-ghost" onClick={onReport}>
                  Report
                </button>
              </Show>
            </div>

            <Show when={isOwn()}>
              <div class="wizard-actions">
                <button
                  type="button"
                  class="btn btn-outline"
                  disabled={statusMutation.isPending || opp.status === "open"}
                  onClick={() => statusMutation.mutate("open")}
                >
                  Reopen
                </button>
                <button
                  type="button"
                  class="btn btn-outline"
                  disabled={statusMutation.isPending || opp.status === "filled"}
                  onClick={() => statusMutation.mutate("filled")}
                >
                  Mark filled
                </button>
                <button
                  type="button"
                  class="btn btn-outline"
                  disabled={statusMutation.isPending || opp.status === "closed"}
                  onClick={() => statusMutation.mutate("closed")}
                >
                  Close
                </button>
              </div>
            </Show>
          </>
        )}
      </Show>
    </section>
  );
}
